/**
 * Left-panel segment pool % distribution — UI-only, not on the backend.
 *
 * When the user edits "% of pool", adds/deletes segments, or reorders them,
 * these helpers keep shares summing to 100% (proportional rebalance + drift fix).
 * The generate API receives the resulting percentages; it does not rebalance them.
 */
import { arrayMove } from '@dnd-kit/sortable';

import type { Segment } from '../types';

/** Keep `order` in sync with list position (1-based). */
export function renumberSegments(segs: Segment[]): Segment[] {
  return segs.map((s, i) => ({ ...s, order: i + 1 }));
}

/** Add `diff` to the largest-pct segment so the shares sum to exactly 100. */
function fixDrift(segs: Segment[]): void {
  const sum = segs.reduce((s, g) => s + g.pct, 0);
  const diff = 100 - sum;
  if (diff === 0) return;
  let maxIdx = 0;
  segs.forEach((s, i) => {
    if (s.pct > segs[maxIdx].pct) maxIdx = i;
  });
  segs[maxIdx].pct = Math.max(1, segs[maxIdx].pct + diff);
}

/**
 * After changing segment `changedIdx` to `newPct`, scale all other segments
 * proportionally so the total share equals 100. Returns a new segments array.
 */
export function rebalancePct(
  segs: Segment[],
  changedIdx: number,
  rawPct: number,
): Segment[] {
  const next = segs.map((s) => ({ ...s }));
  const newPct = Math.min(99, Math.max(1, rawPct));
  const others = next.filter((_, i) => i !== changedIdx);
  const otherTotal = others.reduce((s, g) => s + g.pct, 0);
  const remaining = 100 - newPct;

  next[changedIdx].pct = newPct;

  if (otherTotal === 0) {
    others.forEach((s) => {
      s.pct = Math.round(remaining / others.length);
    });
  } else {
    others.forEach((s) => {
      s.pct = Math.round((s.pct / otherTotal) * remaining);
    });
  }

  // Fix any rounding drift so they sum exactly to 100
  const sum = next.reduce((s, g) => s + g.pct, 0);
  const diff = 100 - sum;
  if (diff !== 0) {
    const firstOther = next.findIndex((_, i) => i !== changedIdx);
    const target = next.reduce(
      (best, s, i) => (i !== changedIdx && s.pct > next[best].pct ? i : best),
      firstOther,
    );
    if (target >= 0) next[target].pct = Math.max(1, next[target].pct + diff);
  }

  return next;
}

/** Add a new segment at the TOP (highest priority); others scale down. */
export function addSegment(segs: Segment[]): Segment[] {
  return addSegmentWithName(segs, 'New segment');
}

/** Add a dealer primary segment by name at the top of the list. */
export function addSegmentWithName(segs: Segment[], segmentName: string): Segment[] {
  const name = segmentName.trim();
  if (!name) return segs;
  const newPct = 10;
  const scale = (100 - newPct) / 100;
  const scaled = segs.map((s) => ({ ...s, pct: Math.max(1, Math.round(s.pct * scale)) }));
  const next: Segment[] = [
    { id: Date.now(), name, pct: newPct, startDay: 1, order: 1 },
    ...scaled,
  ];
  fixDrift(next);
  return renumberSegments(next);
}

/** Delete segment `idx`; freed percentage redistributes proportionally. */
export function deleteSegment(segs: Segment[], idx: number): Segment[] {
  if (segs.length < 2) return segs;
  const freed = segs[idx].pct;
  const next = segs.filter((_, i) => i !== idx).map((s) => ({ ...s }));
  const remTotal = next.reduce((s, g) => s + g.pct, 0);
  next.forEach((s) => {
    s.pct = Math.round((s.pct / remTotal) * (remTotal + freed));
  });
  fixDrift(next);
  return renumberSegments(next);
}

/** Reorder segments after a drag, identifying items by their string id. */
export function reorderSegments(
  segs: Segment[],
  activeId: string,
  overId: string,
): Segment[] {
  const oldIndex = segs.findIndex((s) => String(s.id) === activeId);
  const newIndex = segs.findIndex((s) => String(s.id) === overId);
  if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return segs;
  return renumberSegments(arrayMove(segs, oldIndex, newIndex));
}
